import { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, X, Clock } from "lucide-react";

/**
 * BackupReminderBanner — nudges travellers to run a fresh encrypted backup
 * when their last one is older than the threshold (default: 7 days) or has
 * never happened. Renders only inside the unlocked vault home.
 *
 * Timestamp is stored in localStorage.tp_vault_last_backup_at (ISO string).
 * BackupPanel writes it after a successful local export OR Drive upload.
 *
 * Users can snooze for 3 days — the snooze suppresses the banner until the
 * snooze window elapses (or a new backup lands, which resets the timer).
 */
const STORAGE_KEY = "tp_vault_last_backup_at";
const SNOOZE_KEY = "tp_vault_backup_snoozed_until";
const REMIND_AFTER_DAYS = 7;
const SNOOZE_DAYS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

export function markVaultBackupNow() {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    localStorage.removeItem(SNOOZE_KEY);
  } catch { /* private mode — skip */ }
}

export default function BackupReminderBanner({ onOpenBackup }) {
  const [last, setLast] = useState(null);
  const [snoozed, setSnoozed] = useState(false);

  useEffect(() => {
    try {
      setLast(localStorage.getItem(STORAGE_KEY));
      const s = localStorage.getItem(SNOOZE_KEY);
      setSnoozed(s && new Date(s).getTime() > Date.now());
    } catch { /* ignore */ }
  }, []);

  if (snoozed) return null;

  const lastDate = last ? new Date(last) : null;
  const ageDays = lastDate ? Math.floor((Date.now() - lastDate.getTime()) / DAY_MS) : null;
  const stale = ageDays === null || ageDays >= REMIND_AFTER_DAYS;
  if (!stale) return null;

  const snooze = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, new Date(Date.now() + SNOOZE_DAYS * DAY_MS).toISOString());
      setSnoozed(true);
    } catch { /* ignore */ }
  };

  return (
    <div className="mb-4 flex items-start gap-3 p-3 border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white rounded" data-testid="backup-reminder-banner">
      <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-800 text-slate-900 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-amber-700" />
          {ageDays === null ? "You haven't backed up your vault yet" : `Your last backup was ${ageDays} days ago`}
        </div>
        <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {ageDays === null
            ? "A lost phone means a lost vault — one tap to back up now."
            : "Fresh backups protect you if this device is lost or wiped."}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button onClick={onOpenBackup} data-testid="backup-reminder-cta"
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-700 rounded">
            Back up now
          </button>
          <button onClick={snooze} data-testid="backup-reminder-snooze"
            className="px-3 py-1.5 border border-amber-300 hover:border-amber-500 text-amber-800 text-xs font-700 rounded">
            Remind me later
          </button>
        </div>
      </div>
      <button onClick={snooze} aria-label="Dismiss" data-testid="backup-reminder-close"
        className="text-slate-400 hover:text-slate-700 shrink-0"><X className="w-4 h-4" /></button>
    </div>
  );
}
